<template>
    <div class="scheduled-sms-page">
        <div class="page-container">
            <div class="settings-form">
                <!-- 页面标题和统计 -->
                <div class="header-section">
                    <h2 class="page-title">短信定时投放管理</h2>
                    <div class="stats-section">
                        <el-row :gutter="20">
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon total">
                                        <el-icon>
                                            <Document />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ stats.total_tasks || 0 }}</div>
                                        <div class="stat-label">总任务数</div>
                                    </div>
                                </div>
                            </el-col>
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon active">
                                        <el-icon>
                                            <CircleCheck />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ stats.active_tasks || 0 }}</div>
                                        <div class="stat-label">运行中</div>
                                    </div>
                                </div>
                            </el-col>
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon success">
                                        <el-icon>
                                            <TrendCharts />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ stats.total_deliveries || 0 }}</div>
                                        <div class="stat-label">总投放次数</div>
                                    </div>
                                </div>
                            </el-col>
                            <el-col :span="6">
                                <div class="stat-card">
                                    <div class="stat-icon today">
                                        <el-icon>
                                            <ChatDotRound />
                                        </el-icon>
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-number">{{ stats.total_sms || 0 }}</div>
                                        <div class="stat-label">总短信条数</div>
                                    </div>
                                </div>
                            </el-col>
                        </el-row>
                    </div>
                </div>

                <!-- 操作按钮区域 -->
                <div class="action-bar">
                    <el-button type="primary" size="large" @click="showCreateDialog = true">
                        <el-icon>
                            <Plus />
                        </el-icon>
                        新建短信定时任务
                    </el-button>
                    <el-button type="success" size="large" @click="fetchTaskList">
                        <el-icon>
                            <Refresh />
                        </el-icon>
                        刷新列表
                    </el-button>
                    <el-button type="info" size="large" @click="showLogsDialog = true">
                        <el-icon>
                            <Document />
                        </el-icon>
                        查看投放日志
                    </el-button>
                </div>

                <!-- 搜索区域 -->
                <div class="search-section">
                    <el-form :inline="true" :model="searchForm">
                        <el-form-item label="任务名称">
                            <el-input v-model="searchForm.task_name" placeholder="搜索任务名称" clearable />
                        </el-form-item>
                        <el-form-item label="短信账户">
                            <el-select v-model="searchForm.sms_account_id" placeholder="选择短信账户" clearable>
                                <el-option v-for="account in accountList" :key="account.id"
                                    :label="account.account_name" :value="account.id" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="号码分类">
                            <el-select v-model="searchForm.category_id" placeholder="选择号码分类" clearable>
                                <el-option v-for="category in categoryList" :key="category.id" :label="category.name"
                                    :value="category.id" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="任务状态">
                            <el-select v-model="searchForm.status" placeholder="选择状态" clearable>
                                <el-option label="全部" value="" />
                                <el-option label="启用" :value="1" />
                                <el-option label="停用" :value="0" />
                            </el-select>
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" @click="handleSearch">
                                <el-icon>
                                    <Search />
                                </el-icon>
                                搜索
                            </el-button>
                            <el-button @click="resetSearch">
                                <el-icon>
                                    <Refresh />
                                </el-icon>
                                重置
                            </el-button>
                        </el-form-item>
                    </el-form>
                </div>

                <!-- 任务列表 -->
                <div class="task-list-section">
                    <el-table :data="taskList" stripe v-loading="loading" style="width: 100%">
                        <el-table-column prop="task_name" label="任务名称" min-width="150" show-overflow-tooltip />

                        <el-table-column label="短信账户" width="200" show-overflow-tooltip>
                            <template #default="{ row }">
                                <div class="account-info">
                                    <div class="account-name">{{ row.account_name }}</div>
                                    <div class="account-provider">
                                        <el-tag size="small"
                                            :type="getProviderTagType(getAccountProvider(row.sms_account_id))">
                                            {{ getProviderName(getAccountProvider(row.sms_account_id)) }}
                                        </el-tag>
                                    </div>
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column label="签名和模板" width="200" show-overflow-tooltip>
                            <template #default="{ row }">
                                <div class="sign-template-info">
                                    <div class="sign-name">签名: {{ row.sign_name }}</div>
                                    <div class="template-name">模板: {{ row.template_name }}</div>
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column label="号码分类" width="120">
                            <template #default="{ row }">
                                <span v-if="row.category_name">{{ row.category_name }}</span>
                                <span v-else class="empty-value">无分类</span>
                            </template>
                        </el-table-column>

                        <el-table-column label="投放配置" width="150">
                            <template #default="{ row }">
                                <div class="delivery-config">
                                    <div>每次: {{ row.sms_count_per_delivery }} 条</div>
                                    <div>时段: {{ row.time_periods?.length || 0 }} 个</div>
                                    <div>星期: {{ row.weekdays?.length || 0 }} 天</div>
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column label="任务状态" width="120">
                            <template #default="{ row }">
                                <el-tag :type="row.status ? 'success' : 'info'" effect="light">
                                    {{ row.status ? '运行中' : '已停用' }}
                                </el-tag>
                                <div v-if="row.next_delivery_time" class="next-time">
                                    下次: {{ formatTime(row.next_delivery_time) }}
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column label="投放统计" width="120">
                            <template #default="{ row }">
                                <div class="delivery-stats">
                                    <div>成功: {{ row.success_delivery_count || 0 }}</div>
                                    <div>总计: {{ row.total_delivery_count || 0 }}</div>
                                    <div>短信: {{ row.total_sms_count || 0 }} 条</div>
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column label="最后投放" width="180">
                            <template #default="{ row }">
                                <span v-if="row.last_delivery_time">{{ formatTime(row.last_delivery_time) }}</span>
                                <span v-else class="empty-value">暂无投放</span>
                            </template>
                        </el-table-column>

                        <el-table-column label="操作" width="400" fixed="right">
                            <template #default="{ row }">
                                <div class="action-buttons">
                                    <!-- 复制按钮 -->
                                    <el-button type="success" size="small" @click="copyTask(row)">
                                        <el-icon>
                                            <CopyDocument />
                                        </el-icon>
                                        复制
                                    </el-button>

                                    <el-button type="primary" size="small" @click="editTask(row)">
                                        <el-icon>
                                            <Edit />
                                        </el-icon>
                                        编辑
                                    </el-button>
                                    <el-button :type="row.status ? 'warning' : 'success'" size="small"
                                        @click="toggleTaskStatus(row)">
                                        <el-icon v-if="row.status">
                                            <VideoPause />
                                        </el-icon>
                                        <el-icon v-else>
                                            <CircleCheck />
                                        </el-icon>
                                        {{ row.status ? '暂停' : '启用' }}
                                    </el-button>
                                    <el-button type="danger" size="small" @click="deleteTask(row.id)">
                                        <el-icon>
                                            <Delete />
                                        </el-icon>
                                        删除
                                    </el-button>
                                    <el-button type="info" size="small" @click="manualDelivery(row)">
                                        <el-icon>
                                            <Promotion />
                                        </el-icon>
                                        投放
                                    </el-button>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>

                    <!-- 分页 -->
                    <div class="pagination-container">
                        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
                            :total="pagination.total" :page-sizes="[10, 20, 50, 100]"
                            layout="total, sizes, prev, pager, next, jumper" @size-change="fetchTaskList"
                            @current-change="fetchTaskList" />
                    </div>
                </div>
            </div>
        </div>

        <!-- 创建/编辑任务弹窗 -->
        <el-dialog :title="editMode ? '编辑短信定时任务' : '新建短信定时任务'" v-model="showCreateDialog" width="900px"
            :close-on-click-modal="false">
            <el-form :model="taskForm" label-width="140px" ref="taskFormRef">
                <el-row :gutter="20">
                    <el-col :span="12">
                        <el-form-item label="任务名称" prop="task_name" required>
                            <el-input v-model="taskForm.task_name" placeholder="请输入任务名称" />
                        </el-form-item>
                    </el-col>
                    <el-col :span="12">
                        <el-form-item label="任务状态">
                            <el-switch v-model="taskForm.status" active-text="启用" inactive-text="停用" />
                        </el-form-item>
                    </el-col>
                </el-row>

                <el-form-item label="选择短信账户" prop="sms_account_id" required>
                    <el-select v-model="taskForm.sms_account_id" placeholder="请选择短信账户" style="width: 100%" clearable
                        @change="handleAccountChange">
                        <el-option v-for="account in accountList" :key="account.id"
                            :label="`${account.account_name} (${getProviderName(account.provider)})`"
                            :value="account.id" />
                    </el-select>
                </el-form-item>

                <el-form-item label="选择短信签名" prop="sign_id" required>
                    <el-select v-model="taskForm.sign_id" placeholder="请选择短信签名" style="width: 100%" clearable
                        :disabled="!taskForm.sms_account_id">
                        <el-option v-for="sign in accountSigns" :key="sign.id" :label="sign.sign_name"
                            :value="sign.id" />
                    </el-select>
                </el-form-item>

                <el-form-item label="选择短信模板" prop="template_id" required>
                    <el-select v-model="taskForm.template_id" placeholder="请选择短信模板" style="width: 100%" clearable
                        :disabled="!taskForm.sms_account_id" @change="handleTemplateChange">
                        <el-option v-for="template in accountTemplates" :key="template.id"
                            :label="`${template.template_name}`" :value="template.id">
                            <span style="float: left">{{ template.template_name }}</span>
                            <span style="float: right; color: #8492a6; font-size: 12px">
                                {{ template.template_content && template.template_content.substring(0, 20) }}...
                            </span>
                        </el-option>
                    </el-select>
                </el-form-item>

                <el-form-item label="号码分类" prop="category_id">
                    <el-select v-model="taskForm.category_id" placeholder="请选择号码分类" style="width: 100%" clearable>
                        <el-option v-for="category in categoryList" :key="category.id" :label="category.name"
                            :value="category.id" />
                    </el-select>
                </el-form-item>


                <el-form-item label="模板内容预览" v-if="selectedTemplate">
                    <div class="template-preview">
                        <div class="template-content">{{ selectedTemplate.template_content }}</div>
                        <div v-if="templateVariables.length > 0" class="template-variables">
                            <div class="variables-title">模板变量：</div>
                            <div class="variable-inputs">
                                <div v-for="variable in templateVariables" :key="variable" class="variable-input-item">
                                    <span class="variable-label">{{ variable }}:</span>
                                    <el-input v-model="variableInputs[variable]" :placeholder="`请输入${variable}的值`"
                                        size="small" style="width: 180px; margin-left: 10px" />
                                </div>
                            </div>
                        </div>
                    </div>
                </el-form-item>

                <el-form-item label="每次发送数量" prop="sms_count_per_delivery" required>
                    <el-input-number v-model="taskForm.sms_count_per_delivery" :min="1" :max="100"
                        controls-position="right" />
                    <div class="form-tip">每次发送的短信条数，最多100条</div>
                </el-form-item>

                <!-- 星期选择 -->
                <el-form-item label="发送星期" prop="weekdays" required>
                    <div class="weekdays-container">
                        <el-checkbox-group v-model="taskForm.weekdays">
                            <el-checkbox v-for="day in weekdayOptions" :key="day.value" :label="day.value">
                                {{ day.label }}
                            </el-checkbox>
                        </el-checkbox-group>
                        <div class="form-tip">选择短信发送的星期，未选择的星期将不发送</div>
                    </div>
                </el-form-item>

                <el-form-item label="发送时间段" prop="time_periods" required>
                    <div class="time-periods-container">
                        <div class="time-periods-header">
                            <span>时间段配置</span>
                            <el-button type="primary" text @click="addTimePeriod">
                                <el-icon>
                                    <Plus />
                                </el-icon>
                                添加时间段
                            </el-button>
                        </div>

                        <div class="time-periods-list">
                            <div v-for="(period, index) in taskForm.time_periods" :key="index" class="period-item">
                                <div class="period-time">
                                    <el-time-picker v-model="period.start_time" placeholder="开始时间" format="HH:mm"
                                        value-format="HH:mm" style="width: 120px" />
                                    <span class="time-separator">至</span>
                                    <el-time-picker v-model="period.end_time" placeholder="结束时间" format="HH:mm"
                                        value-format="HH:mm" style="width: 120px" />
                                </div>
                                <div class="period-count">
                                    <el-input-number v-model="period.max_deliveries" :min="1" :max="50"
                                        placeholder="最大次数" controls-position="right" />
                                    <span class="count-label">次/时段</span>
                                </div>
                                <el-button text @click="removeTimePeriod(index)"
                                    :disabled="taskForm.time_periods.length === 1">
                                    <el-icon>
                                        <Delete />
                                    </el-icon>
                                </el-button>
                            </div>
                        </div>

                        <div class="form-tip">系统将在设定的时间段内自动发送短信，每个时段不超过设定的最大次数</div>
                    </div>
                </el-form-item>

                <el-form-item label="备注">
                    <el-input v-model="taskForm.notes" type="textarea" :rows="3" placeholder="请输入任务备注信息" />
                </el-form-item>
            </el-form>

            <template #footer>
                <el-button @click="showCreateDialog = false">取消</el-button>
                <el-button type="primary" @click="submitTask" :loading="submitting">
                    {{ editMode ? '更新任务' : '创建任务' }}
                </el-button>
            </template>
        </el-dialog>

        <!-- 投放日志弹窗 -->
        <el-dialog title="短信投放日志" v-model="showLogsDialog" width="1050px" top="5vh" @open="fetchDeliveryLogs">
            <div class="logs-content">
                <div class="logs-filter">
                    <el-form :inline="true" :model="logsFilter">
                        <el-form-item label="任务">
                            <el-select v-model="logsFilter.task_id" placeholder="选择任务" clearable style="width:150px">
                                <el-option v-for="task in taskList" :key="task.id" :label="task.task_name"
                                    :value="task.id" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="日期">
                            <el-date-picker v-model="logsFilter.date_range" type="daterange" range-separator="至"
                                start-placeholder="开始日期" end-placeholder="结束日期" value-format="YYYY-MM-DD" />
                        </el-form-item>
                        <el-form-item>
                            <el-button type="primary" @click="fetchDeliveryLogs">搜索</el-button>
                            <el-button @click="resetLogsFilter">重置</el-button>
                        </el-form-item>
                    </el-form>
                </div>

                <el-table :data="deliveryLogs" v-loading="logsLoading" height="500">
                    <el-table-column prop="task_name" label="任务名称" width="150" />
                    <el-table-column prop="account_name" label="短信账户" width="150" />
                    <el-table-column prop="sign_name" label="短信签名" width="120" />
                    <el-table-column prop="template_name" label="短信模板" width="200" show-overflow-tooltip />
                    <el-table-column prop="category_name" label="号码分类" width="120" />
                    <el-table-column label="发送数量" width="120">
                        <template #default="{ row }">
                            {{ row.delivery_count }} 条
                        </template>
                    </el-table-column>
                    <el-table-column label="发送结果" width="120">
                        <template #default="{ row }">
                            <el-tag :type="row.fail_count > 0 ? 'warning' : 'success'">
                                成功: {{ row.success_count }} 失败: {{ row.fail_count }}
                            </el-tag>
                        </template>
                    </el-table-column>
                    <el-table-column label="短信条数" width="100">
                        <template #default="{ row }">
                            {{ row.sms_count }} 条
                        </template>
                    </el-table-column>
                    <el-table-column prop="delivery_time" label="发送时间" width="180">
                        <template #default="{ row }">
                            {{ formatTime(row.delivery_time) }}
                        </template>
                    </el-table-column>
                    <el-table-column label="操作" width="100">
                        <template #default="{ row }">
                            <el-button type="primary" text @click="viewLogDetail(row)">详情</el-button>
                        </template>
                    </el-table-column>
                </el-table>

                <div class="pagination-container">
                    <el-pagination v-model:current-page="logsPagination.page"
                        v-model:page-size="logsPagination.pageSize" :total="logsPagination.total"
                        :page-sizes="[10, 20, 50]" layout="total, sizes, prev, pager, next"
                        @size-change="fetchDeliveryLogs" @current-change="fetchDeliveryLogs" />
                </div>
            </div>
        </el-dialog>

        <!-- 日志详情弹窗 -->
        <el-dialog title="短信发送详情" v-model="showLogDetailDialog" width="600px">
            <div v-if="currentLogDetail" class="log-detail">
                <el-descriptions :column="1" border>
                    <el-descriptions-item label="任务名称">{{ currentLogDetail.task_name }}</el-descriptions-item>
                    <el-descriptions-item label="短信账户">{{ currentLogDetail.account_name }}</el-descriptions-item>
                    <el-descriptions-item label="短信签名">{{ currentLogDetail.sign_name }}</el-descriptions-item>
                    <el-descriptions-item label="短信模板">{{ currentLogDetail.template_name }}</el-descriptions-item>
                    <el-descriptions-item label="发送时间">{{ formatTime(currentLogDetail.delivery_time)
                    }}</el-descriptions-item>
                    <el-descriptions-item label="发送结果">
                        成功: {{ currentLogDetail.success_count }} 条,
                        失败: {{ currentLogDetail.fail_count }} 条
                    </el-descriptions-item>
                    <el-descriptions-item label="短信条数">
                        {{ currentLogDetail.sms_count }} 条
                    </el-descriptions-item>
                    <el-descriptions-item label="错误信息" v-if="currentLogDetail.error_message">
                        <el-alert :title="currentLogDetail.error_message" type="error" :closable="false" />
                    </el-descriptions-item>
                </el-descriptions>

                <div v-if="currentLogDetail.details && currentLogDetail.details.fail_list" class="fail-list">
                    <h4>失败手机号列表:</h4>
                    <el-tag v-for="(fail, index) in currentLogDetail.details.fail_list" :key="index" type="danger"
                        class="fail-tag">
                        {{ fail.phone }}: {{ fail.error }}
                    </el-tag>
                </div>
            </div>
        </el-dialog>
    </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
    Delete, Refresh, Document,
    CircleCheck, ChatDotRound,
    VideoPause, Promotion, Plus,
    Edit, TrendCharts, CopyDocument,
    Search
} from '@element-plus/icons-vue'

export default {
    name: 'SmsScheduled',
    components: {
        Delete, Refresh, Document,
        CircleCheck, ChatDotRound,
        VideoPause, Promotion, Plus,
        Edit, TrendCharts, CopyDocument,
        Search
    },
    data() {
        return {
            loading: false,
            submitting: false,
            logsLoading: false,
            templateVariables: [],
            variableInputs: {},
            accountProviderMap: {},
            // 任务列表数据
            taskList: [],
            pagination: {
                page: 1,
                pageSize: 10,
                total: 0
            },

            // 搜索表单
            searchForm: {
                task_name: '',
                sms_account_id: '',
                category_id: '',
                status: ''
            },

            // 统计信息
            stats: {},

            // 弹窗控制
            showCreateDialog: false,
            showLogsDialog: false,
            showLogDetailDialog: false,
            editMode: false,

            // 表单数据
            taskForm: {
                task_name: '',
                sms_account_id: '',
                sign_id: '',
                template_id: '',
                category_id: '',
                sms_count_per_delivery: 10,
                time_periods: [
                    { start_time: '09:00', end_time: '12:00', max_deliveries: 3 },
                    { start_time: '14:00', end_time: '18:00', max_deliveries: 3 }
                ],
                weekdays: [1, 2, 3, 4, 5], // 默认周一到周五
                status: true,
                notes: ''
            },

            // 星期选项
            weekdayOptions: [
                { label: '星期一', value: 1 },
                { label: '星期二', value: 2 },
                { label: '星期三', value: 3 },
                { label: '星期四', value: 4 },
                { label: '星期五', value: 5 },
                { label: '星期六', value: 6 },
                { label: '星期日', value: 7 }
            ],

            // 选项数据
            accountList: [],
            categoryList: [],
            accountSigns: [],
            accountTemplates: [],
            selectedTemplate: null,

            // 日志相关
            deliveryLogs: [],
            currentLogDetail: null,
            logsFilter: {
                task_id: '',
                date_range: []
            },
            logsPagination: {
                page: 1,
                pageSize: 10,
                total: 0
            }
        }
    },
    computed: {
        userInfo() {
            return JSON.parse(Cookies.get('user_info') || '{}')
        }
    },
    mounted() {
        this.fetchTaskList();
        this.fetchStats();
        this.fetchAccountList();
        this.fetchCategoryList();
    },
    methods: {
        // 获取服务商名称
        getProviderName(provider) {
            const providerMap = {
                'aliyun': '阿里云',
                'tencent': '腾讯云',
                'zhangjun': '掌骏云',
                'feige': '飞鸽云',
                'custom': '自定义'
            };
            return providerMap[provider] || provider;
        },

        // 获取服务商标签类型
        getProviderTagType(provider) {
            const typeMap = {
                'aliyun': 'primary',
                'tencent': 'success',
                'zhangjun': 'warning',
                'feige': 'info',
                'custom': 'danger'
            };
            return typeMap[provider] || 'info';
        },

        // 获取账户的服务商信息
        getAccountProvider(accountId) {
            if (this.accountProviderMap[accountId]) {
                return this.accountProviderMap[accountId];
            }

            // 从账户列表中查找
            const account = this.accountList.find(acc => acc.id === accountId);
            if (account) {
                this.accountProviderMap[accountId] = account.provider;
                return account.provider;
            }

            return '';
        },
        // 同时修改 handleAccountChange 方法，确保正确处理异步加载
        async handleAccountChange(accountId) {
            if (!accountId) {
                this.accountSigns = [];
                this.accountTemplates = [];
                // 注意：这里不要清空 taskForm 中的 sign_id 和 template_id
                this.selectedTemplate = null;
                this.templateVariables = [];
                return;
            }

            try {
                // 使用 Promise.all 并行加载签名和模板
                const [signsResponse, templatesResponse] = await Promise.all([
                    axios.post(`${config.BASE_URL}/shop/api/scheduled_sms.php`, {
                        action: 'get_account_signs',
                        store_id: this.userInfo.id,
                        account_id: accountId
                    }),
                    axios.post(`${config.BASE_URL}/shop/api/scheduled_sms.php`, {
                        action: 'get_account_templates',
                        store_id: this.userInfo.id,
                        account_id: accountId
                    })
                ]);

                if (signsResponse.data.data) {
                    this.accountSigns = signsResponse.data.data.filter(sign => sign.audit_status === 'AUDIT_STATE_PASS');
                }

                if (templatesResponse.data.data) {
                    this.accountTemplates = templatesResponse.data.data.filter(template => template.audit_status === 'AUDIT_STATE_PASS');
                }

                // 仅在新建任务时重置选中的签名和模板
                if (!this.editMode) {
                    this.taskForm.sign_id = '';
                    this.taskForm.template_id = '';
                    this.selectedTemplate = null;
                    this.templateVariables = [];
                }

            } catch (error) {
                console.error('加载账户数据失败:', error);
                this.$message.error('加载账户数据失败');
                this.accountSigns = [];
                this.accountTemplates = [];

                // 如果编辑模式中加载失败，也要清空选择
                if (this.editMode) {
                    this.taskForm.sign_id = '';
                    this.taskForm.template_id = '';
                    this.selectedTemplate = null;
                    this.templateVariables = [];
                }
            }
        },

        // 修改 handleTemplateChange 方法，添加参数支持已有变量值
        handleTemplateChange(templateId, existingVariables = {}) {
            console.log('选择的模板ID:', templateId);
            console.log('已有变量值:', existingVariables);

            if (!templateId) {
                this.selectedTemplate = null;
                this.templateVariables = [];
                this.variableInputs = {};
                return;
            }

            const template = this.accountTemplates.find(t => t.id === templateId);
            if (template) {
                this.selectedTemplate = template;
                console.log('选中的模板:', this.selectedTemplate);
                console.log('模板内容:', this.selectedTemplate.template_content);

                // 解析模板变量
                this.parseTemplateVariables(this.selectedTemplate.template_content);

                // 初始化变量输入值，如果有已有值则使用已有值
                if (this.templateVariables.length > 0) {
                    this.variableInputs = {};
                    this.templateVariables.forEach(variable => {
                        // 从已有值中获取，如果没有则设置为空
                        this.variableInputs[variable] = existingVariables[variable] || '';
                    });
                    console.log('初始化后的变量输入值:', this.variableInputs);
                }
            } else {
                this.selectedTemplate = null;
                this.templateVariables = [];
                this.variableInputs = {};
            }
        },


        // 修改 parseTemplateVariables 方法
        parseTemplateVariables(content) {
            if (!content) {
                this.templateVariables = [];
                this.variableInputs = {};
                return [];
            }

            const variables = [];
            console.log('开始解析模板内容:', content);

            // 方法1：匹配单独的 $$ 占位符
            const dollarMatches = content.match(/\$\$/g);
            console.log('找到的 $$ 数量:', dollarMatches ? dollarMatches.length : 0);

            if (dollarMatches) {
                // 为每个 $$ 生成变量名
                dollarMatches.forEach((match, index) => {
                    const varName = index === 0 ? 'code' : `code${index + 1}`;
                    if (!variables.includes(varName)) {
                        variables.push(varName);
                        console.log('添加变量:', varName);
                    }
                });
            }

            // 方法2：匹配 ${variable} 格式
            const matches1 = content.match(/\$\{(\w+)\}/g);
            if (matches1) {
                matches1.forEach(match => {
                    const varName = match.replace(/\$\{(\w+)\}/, '$1');
                    if (!variables.includes(varName)) {
                        variables.push(varName);
                        console.log('添加 ${} 变量:', varName);
                    }
                });
            }

            // 方法3：匹配 {variable} 格式  
            const matches2 = content.match(/\{(\w+)\}/g);
            if (matches2) {
                matches2.forEach(match => {
                    const varName = match.replace(/\{(\w+)\}/, '$1');
                    if (!variables.includes(varName)) {
                        variables.push(varName);
                        console.log('添加 {} 变量:', varName);
                    }
                });
            }

            // 方法4：匹配 {%variable%} 格式
            const matches3 = content.match(/\{%(\w+)%\}/g);
            if (matches3) {
                matches3.forEach(match => {
                    const varName = match.replace(/\{%(\w+)%\}/, '$1');
                    if (!variables.includes(varName)) {
                        variables.push(varName);
                        console.log('添加 {%%} 变量:', varName);
                    }
                });
            }

            console.log('最终解析到的变量:', variables);
            this.templateVariables = variables;

            return variables;
        },
        // 复制任务
        copyTask(task) {
            this.editMode = false;

            // 解析 JSON 字符串或使用数组
            let timePeriods = task.time_periods;
            let weekdays = task.weekdays;

            if (typeof task.time_periods === 'string') {
                try {
                    timePeriods = JSON.parse(task.time_periods);
                } catch (e) {
                    console.error('解析 time_periods 失败:', e);
                    timePeriods = [{ start_time: '09:00', end_time: '12:00', max_deliveries: 3 }];
                }
            }

            if (typeof task.weekdays === 'string') {
                try {
                    weekdays = JSON.parse(task.weekdays);
                } catch (e) {
                    console.error('解析 weekdays 失败:', e);
                    weekdays = [1, 2, 3, 4, 5];
                }
            }

            // 复制任务数据，但清除ID和相关统计信息
            this.taskForm = {
                task_name: `${task.task_name}_副本`,
                sms_account_id: task.sms_account_id,
                sign_id: task.sign_id,
                template_id: task.template_id,
                category_id: task.category_id,
                sms_count_per_delivery: task.sms_count_per_delivery,
                time_periods: [...timePeriods],  // 确保是数组
                weekdays: [...weekdays],         // 确保是数组
                status: true, // 复制后默认启用
                notes: task.notes ? `${task.notes} (副本)` : ''
            };

            // 加载账户相关的签名和模板
            if (task.sms_account_id) {
                this.handleAccountChange(task.sms_account_id).then(() => {
                    // 稍后设置选中的签名和模板
                    setTimeout(() => {
                        this.taskForm.sign_id = task.sign_id;
                        this.taskForm.template_id = task.template_id;
                        this.handleTemplateChange(task.template_id);
                    }, 300);
                });
            }

            this.showCreateDialog = true;
        },

        // 搜索任务
        handleSearch() {
            this.pagination.page = 1;
            this.fetchTaskList();
        },

        // 重置搜索
        resetSearch() {
            this.searchForm = {
                task_name: '',
                sms_account_id: '',
                category_id: '',
                status: ''
            };
            this.pagination.page = 1;
            this.fetchTaskList();
        },

        // 获取任务列表
        async fetchTaskList() {
            this.loading = true;
            try {
                const params = {
                    action: 'get_tasks',
                    store_id: this.userInfo.id,
                    page: this.pagination.page,
                    page_size: this.pagination.pageSize
                };

                // 添加搜索条件
                if (this.searchForm.task_name) {
                    params.task_name = this.searchForm.task_name;
                }
                if (this.searchForm.sms_account_id) {
                    params.sms_account_id = this.searchForm.sms_account_id;
                }
                if (this.searchForm.category_id) {
                    params.category_id = this.searchForm.category_id;
                }
                if (this.searchForm.status !== '') {
                    params.status = this.searchForm.status;
                }

                const response = await axios.post(`${config.BASE_URL}/shop/api/scheduled_sms.php`, params);

                if (response.data.data) {
                    this.taskList = response.data.data;
                    this.pagination.total = response.data.pagination?.total || 0;
                }
            } catch (error) {
                this.$message.error('获取任务列表失败');
                console.error('获取任务列表错误:', error);
            } finally {
                this.loading = false;
            }
        },

        // 获取统计信息
        async fetchStats() {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/scheduled_sms.php`, {
                    action: 'get_task_stats',
                    store_id: this.userInfo.id
                });

                if (response.data) {
                    this.stats = response.data;
                }
            } catch (error) {
                console.error('获取统计信息失败:', error);
            }
        },

        // 获取短信账户列表
        async fetchAccountList() {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/sms_account.php`, {
                    action: 'list',
                    store_id: this.userInfo.id,
                    page: 1,
                    page_size: 1000
                });

                if (response.data.data) {
                    this.accountList = response.data.data.filter(account => account.is_enabled === 1);
                }
            } catch (error) {
                console.error('获取短信账户列表失败:', error);
            }
        },

        // 获取号码分类列表
        async fetchCategoryList() {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/number_management_category.php`, {
                    action: 'list_all',
                    store_id: this.userInfo.id
                });

                if (response.data.data) {
                    this.categoryList = response.data.data;
                }
            } catch (error) {
                console.error('获取号码分类失败:', error);
                this.$message.error('获取号码分类失败');
            }
        },

        // 添加时间段
        addTimePeriod() {
            this.taskForm.time_periods.push({
                start_time: '09:00',
                end_time: '12:00',
                max_deliveries: 3
            });
        },

        // 删除时间段
        removeTimePeriod(index) {
            if (this.taskForm.time_periods.length > 1) {
                this.taskForm.time_periods.splice(index, 1);
            } else {
                this.$message.warning('至少需要保留一个时间段');
            }
        },

        // 提交任务
        async submitTask() {
            if (!this.taskForm.task_name.trim()) {
                this.$message.warning('请输入任务名称');
                return;
            }

            if (!this.taskForm.sms_account_id) {
                this.$message.warning('请选择短信账户');
                return;
            }

            if (!this.taskForm.sign_id) {
                this.$message.warning('请选择短信签名');
                return;
            }

            if (!this.taskForm.template_id) {
                this.$message.warning('请选择短信模板');
                return;
            }

            if (this.taskForm.weekdays.length === 0) {
                this.$message.warning('请至少选择一个发送星期');
                return;
            }

            if (this.taskForm.time_periods.length === 0) {
                this.$message.warning('请至少设置一个发送时间段');
                return;
            }

            this.submitting = true;
            try {
                // 如果是模板任务且有变量，构建变量数据
                let templateVariables = {};
                if (this.templateVariables.length > 0 && this.variableInputs) {
                    // 只收集有值的变量
                    Object.keys(this.variableInputs).forEach(key => {
                        if (this.variableInputs[key]) {
                            templateVariables[key] = this.variableInputs[key];
                        }
                    });
                }

                const formData = {
                    ...this.taskForm,
                    store_id: this.userInfo.id,
                    status: this.taskForm.status ? 1 : 0,
                    time_periods: JSON.stringify(this.taskForm.time_periods),
                    weekdays: JSON.stringify(this.taskForm.weekdays),
                    template_variables: Object.keys(templateVariables).length > 0 ? JSON.stringify(templateVariables) : '{}'
                };

                // 如果是编辑模式，需要包含 ID
                if (this.editMode) {
                    formData.id = this.taskForm.id;
                }
                const action = this.editMode ? 'update_task' : 'create_task';
                const response = await axios.post(`${config.BASE_URL}/shop/api/scheduled_sms.php`, {
                    action: action,
                    ...formData
                });

                if (response.data.success) {
                    this.$message.success(this.editMode ? '任务更新成功' : '任务创建成功');
                    this.showCreateDialog = false;
                    this.fetchTaskList();
                    this.fetchStats();
                    this.resetTaskForm();
                } else {
                    this.$message.error(response.data.error || '操作失败');
                }
            } catch (error) {
                this.$message.error('操作失败');
                console.error('提交任务错误:', error);
            } finally {
                this.submitting = false;
            }
        },

        // 编辑任务
        async editTask(task) {
            this.editMode = true;

            // 解析 JSON 字符串或使用数组
            let timePeriods = task.time_periods;
            let weekdays = task.weekdays;

            // 获取存储的模板变量
            let storedTemplateVariables = {};
            if (task.template_variables) {
                try {
                    if (typeof task.template_variables === 'string') {
                        storedTemplateVariables = JSON.parse(task.template_variables);
                    } else {
                        storedTemplateVariables = task.template_variables;
                    }
                } catch (e) {
                    console.error('解析模板变量失败:', e);
                    storedTemplateVariables = {};
                }
            }

            if (typeof task.time_periods === 'string') {
                try {
                    timePeriods = JSON.parse(task.time_periods);
                } catch (e) {
                    console.error('解析 time_periods 失败:', e);
                    timePeriods = [{ start_time: '09:00', end_time: '12:00', max_deliveries: 3 }];
                }
            }

            if (typeof task.weekdays === 'string') {
                try {
                    weekdays = JSON.parse(task.weekdays);
                } catch (e) {
                    console.error('解析 weekdays 失败:', e);
                    weekdays = [1, 2, 3, 4, 5];
                }
            }

            // 先设置基本表单数据
            this.taskForm = {
                id: task.id,
                task_name: task.task_name,
                sms_account_id: task.sms_account_id,
                sign_id: task.sign_id,
                template_id: task.template_id,
                category_id: task.category_id,
                sms_count_per_delivery: task.sms_count_per_delivery,
                time_periods: timePeriods,
                weekdays: weekdays,
                status: task.status === 1,
                notes: task.notes || ''
            };

            // 加载账户相关的签名和模板
            if (task.sms_account_id) {
                try {
                    await this.handleAccountChange(task.sms_account_id);

                    // 等待签名和模板加载完成后再设置选中值
                    await this.$nextTick();

                    // 手动查找并设置签名和模板
                    const signExists = this.accountSigns.some(sign => sign.id === task.sign_id);
                    if (!signExists) {
                        console.warn('签名不存在或已失效，清空签名选择');
                        this.taskForm.sign_id = '';
                    }

                    const templateExists = this.accountTemplates.some(template => template.id === task.template_id);
                    if (templateExists) {
                        // 设置模板并触发相关处理
                        this.handleTemplateChange(task.template_id);

                        // 重要：设置变量输入框的值
                        // 等待模板变量解析完成
                        setTimeout(() => {
                            if (this.templateVariables.length > 0 && storedTemplateVariables) {
                                // 初始化变量输入值
                                this.variableInputs = {};
                                this.templateVariables.forEach(variable => {
                                    // 从存储的值中获取，如果没有则设置为空
                                    this.variableInputs[variable] = storedTemplateVariables[variable] || '';
                                });
                                console.log('初始化变量输入值:', this.variableInputs);
                            }
                        }, 100);
                    } else {
                        console.warn('模板不存在或已失效，清空模板选择');
                        this.taskForm.template_id = '';
                        this.selectedTemplate = null;
                        this.templateVariables = [];
                        this.variableInputs = {};
                    }
                } catch (error) {
                    console.error('加载账户数据失败:', error);
                    this.$message.error('加载账户数据失败');
                }
            } else {
                // 如果没有账户ID，直接设置模板变量
                if (storedTemplateVariables) {
                    this.variableInputs = { ...storedTemplateVariables };
                    console.log('直接设置变量输入值:', this.variableInputs);
                }
            }

            this.showCreateDialog = true;
        },
        // 重置表单
        resetTaskForm() {
            this.taskForm = {
                task_name: '',
                sms_account_id: '',
                sign_id: '',
                template_id: '',
                category_id: '',
                sms_count_per_delivery: 10,
                time_periods: [
                    { start_time: '09:00', end_time: '12:00', max_deliveries: 3 },
                    { start_time: '14:00', end_time: '18:00', max_deliveries: 3 }
                ],
                weekdays: [1, 2, 3, 4, 5],
                status: true,
                notes: ''
            };
            this.editMode = false;
            this.accountSigns = [];
            this.accountTemplates = [];
            this.selectedTemplate = null;
            this.templateVariables = [];
        },

        // 切换任务状态
        async toggleTaskStatus(task) {
            try {
                const response = await axios.post(`${config.BASE_URL}/shop/api/scheduled_sms.php`, {
                    action: 'toggle_task_status',
                    store_id: this.userInfo.id,
                    task_id: task.id,
                    status: task.status ? 0 : 1
                });

                if (response.data.success) {
                    this.$message.success(task.status ? '任务已暂停' : '任务已启用');
                    this.fetchTaskList();
                    this.fetchStats();
                } else {
                    this.$message.error(response.data.error || '操作失败');
                }
            } catch (error) {
                this.$message.error('操作失败');
                console.error('切换任务状态错误:', error);
            }
        },

        // 删除任务
        async deleteTask(taskId) {
            try {
                await this.$confirm('确定要删除这个短信定时任务吗？', '确认删除', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'warning'
                });

                const response = await axios.post(`${config.BASE_URL}/shop/api/scheduled_sms.php`, {
                    action: 'delete_task',
                    store_id: this.userInfo.id,
                    task_id: taskId
                });

                if (response.data.success) {
                    this.$message.success('任务删除成功');
                    this.fetchTaskList();
                    this.fetchStats();
                } else {
                    this.$message.error(response.data.error || '删除失败');
                }
            } catch (error) {
                if (error !== 'cancel') {
                    this.$message.error('删除失败');
                    console.error('删除任务错误:', error);
                }
            }
        },

        // 立即发送
        async manualDelivery(task) {
            try {
                await this.$confirm('确定要立即执行短信发送吗？', '确认发送', {
                    confirmButtonText: '确定',
                    cancelButtonText: '取消',
                    type: 'info'
                });

                const response = await axios.post(`${config.BASE_URL}/shop/api/scheduled_sms.php`, {
                    action: 'manual_delivery',
                    store_id: this.userInfo.id,
                    task_id: task.id
                });

                if (response.data.success) {
                    this.$message.success('短信发送任务已执行');
                    this.fetchTaskList();
                    this.fetchStats();
                } else {
                    this.$message.error(response.data.error || '发送失败');
                }
            } catch (error) {
                if (error !== 'cancel') {
                    this.$message.error('发送失败');
                    console.error('立即发送错误:', error);
                }
            }
        },

        // 获取投放日志
        async fetchDeliveryLogs() {
            this.logsLoading = true;
            try {
                const params = {
                    action: 'get_delivery_logs',
                    store_id: this.userInfo.id,
                    page: this.logsPagination.page,
                    page_size: this.logsPagination.pageSize
                };

                if (this.logsFilter.task_id) {
                    params.task_id = this.logsFilter.task_id;
                }
                if (this.logsFilter.date_range && this.logsFilter.date_range.length === 2) {
                    params.start_date = this.logsFilter.date_range[0];
                    params.end_date = this.logsFilter.date_range[1];
                }

                const response = await axios.post(`${config.BASE_URL}/shop/api/scheduled_sms.php`, params);

                if (response.data.data) {
                    this.deliveryLogs = response.data.data;
                    this.logsPagination.total = response.data.pagination?.total || 0;
                }
            } catch (error) {
                this.$message.error('获取投放日志失败');
                console.error('获取投放日志错误:', error);
            } finally {
                this.logsLoading = false;
            }
        },

        // 重置日志筛选
        resetLogsFilter() {
            this.logsFilter = {
                task_id: '',
                date_range: []
            };
            this.fetchDeliveryLogs();
        },

        // 查看日志详情
        viewLogDetail(log) {
            this.currentLogDetail = log;
            this.showLogDetailDialog = true;
        },

        // 工具函数
        formatTime(time) {
            if (!time) return '-';
            return new Date(time).toLocaleString('zh-CN');
        }
    }
}
</script>

<style scoped lang="scss">
.scheduled-sms-page {
    padding: 20px;
}

.settings-form {
    max-width: 95%;
    margin: 0 auto;
    padding: 32px 40px;
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
    font-size: 24px;
    color: #1f2937;
    margin-bottom: 32px;
    font-weight: 600;
    text-align: center;
    letter-spacing: 0.5px;
}

.header-section {
    margin-bottom: 24px;
}

.stats-section {
    margin-bottom: 24px;
}

.stat-card {
    display: flex;
    align-items: center;
    padding: 20px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);

    .stat-icon {
        width: 50px;
        height: 50px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 16px;
        font-size: 24px;
        color: white;

        &.total {
            background: linear-gradient(135deg, #409EFF, #67C2EF);
        }

        &.active {
            background: linear-gradient(135deg, #67C23A, #85CE61);
        }

        &.success {
            background: linear-gradient(135deg, #E6A23C, #EBB563);
        }

        &.today {
            background: linear-gradient(135deg, #F56C6C, #F78989);
        }
    }

    .stat-content {
        flex: 1;

        .stat-number {
            font-size: 24px;
            font-weight: 700;
            color: #303133;
            margin-bottom: 4px;
        }

        .stat-label {
            font-size: 14px;
            color: #909399;
        }
    }
}

.action-bar {
    display: flex;
    gap: 12px;
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;
}

.search-section {
    margin-bottom: 24px;
    padding: 16px;
    background: #f8fafc;
    border-radius: 8px;

    :deep(.el-form-item__content) {
        .el-select {
            width: 180px !important;
            /* 设置下拉框宽度 */
        }
    }

    .el-form {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        align-items: center;

        .el-form-item {
            margin-bottom: 0;

            /* 针对每个筛选框可以设置不同宽度 */
            &:nth-child(1) .el-select {
                /* 短信账户 */
                width: 200px !important;
            }

            &:nth-child(2) .el-select {
                /* 号码分类 */
                width: 150px !important;
            }

            &:nth-child(3) .el-select {
                /* 任务状态 */
                width: 120px !important;
            }
        }
    }
}

.task-list-section {
    margin-top: 20px;
}

.account-info {
    .account-name {
        font-weight: 500;
        margin-bottom: 4px;
    }

    .account-provider {
        .el-tag {
            font-size: 10px;
            padding: 0 6px;
            height: 20px;
            line-height: 20px;
        }
    }
}

.sign-template-info {
    .sign-name {
        font-weight: 500;
        margin-bottom: 4px;
    }

    .template-name {
        color: #909399;
        font-size: 12px;
    }
}

.template-preview {
    padding: 12px;
    background: #f8fafc;
    border-radius: 6px;
    border: 1px solid #ebeef5;

    .template-content {
        color: #606266;
        line-height: 1.6;
        margin-bottom: 8px;
    }

    .template-variables {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        align-items: center;

        .variables-title {
            font-size: 12px;
            color: #909399;
            margin-right: 8px;
        }

        .variable-tag {
            font-size: 11px;
            padding: 0 6px;
            height: 22px;
            line-height: 22px;
        }
    }
}

.delivery-config,
.delivery-stats {
    font-size: 12px;
    color: #606266;
    line-height: 1.4;
}

.next-time {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
}

.empty-value {
    color: #909399;
    font-style: italic;
}

.pagination-container {
    margin-top: 24px;
    display: flex;
    justify-content: flex-end;
}

/* 星期选择样式 */
.weekdays-container {
    .el-checkbox-group {
        display: flex;
        flex-wrap: wrap;
        gap: 12px;
    }
}

/* 时间段配置样式 */
.time-periods-container {
    border: 1px solid #ebeef5;
    border-radius: 6px;
    padding: 16px;
    background: #fafafa;
}

.time-periods-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    font-weight: 500;
    color: #606266;
}

.time-periods-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.period-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background: white;
    border-radius: 4px;
    border: 1px solid #e4e7ed;
}

.period-time {
    display: flex;
    align-items: center;
    gap: 8px;
}

.time-separator {
    color: #909399;
    font-size: 14px;
}

.period-count {
    display: flex;
    align-items: center;
    gap: 8px;
}

.count-label {
    font-size: 12px;
    color: #909399;
    white-space: nowrap;
}

.form-tip {
    font-size: 12px;
    color: #909399;
    margin-top: 4px;
}

/* 日志相关样式 */
.logs-content {
    .logs-filter {
        margin-bottom: 16px;
        padding: 16px;
        background: #f8fafc;
        border-radius: 6px;
    }
}

.fail-list {
    margin-top: 16px;

    h4 {
        margin-bottom: 8px;
        color: #606266;
    }

    .fail-tag {
        margin: 4px;
        max-width: 100%;
        word-break: break-all;
    }
}

/* 响应式设计 */
@media (max-width: 1200px) {
    .settings-form {
        max-width: 100%;
        padding: 20px;
    }

    .period-item {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }

    .period-time,
    .period-count {
        justify-content: center;
    }

    .search-section {
        .el-form {
            flex-direction: column;
            align-items: stretch;

            .el-form-item {
                width: 100%;

                :deep(.el-form-item__content) {
                    width: 100%;
                }
            }
        }
    }
}

/* 表格样式优化 */
.el-table {
    border: 1px solid #ebeef5;
    border-radius: 8px;
    overflow: hidden;

    :deep(.el-table__header) th {
        background-color: #f5f7fa;
        color: #606266;
        font-weight: 600;
    }

    :deep(.el-table__body) tr:hover>td {
        background-color: #f0f7ff !important;
    }
}

.action-buttons {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;

    .el-button {
        flex: 1;
        min-width: 60px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 12px;

        .el-icon {
            margin-right: 2px;
        }
    }
}

/* 响应式设计 */
@media (max-width: 1200px) {
    .action-buttons {
        gap: 4px;

        .el-button {
            min-width: 50px;
            font-size: 11px;

            .el-icon {
                margin-right: 1px;
            }
        }
    }
}

.variable-inputs {
    margin-top: 8px;
}

.variable-input-item {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
}

.variable-label {
    min-width: 80px;
    font-weight: 500;
    font-size: 13px;
    color: #606266;
}
</style>